import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  WEB_NAV_SIDEBAR_RAIL_S,
  WEB_PRODUCT_GRID_IMAGE_MAX_WIDTH,
} from '../../../theme/webLayoutWidths';

import {
  FlatList,
  NativeScrollEvent,
  NativeSyntheticEvent,
  Pressable,
  StyleSheet,
  Platform,
  Text,
  TextInput,
  View,
  ScrollView,
  ToastAndroid,
} from 'react-native';
import { FlashList, type FlashListRef } from '@shopify/flash-list';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import {
  useFocusEffect,
  useIsFocused,
  useNavigation,
  useRoute,
} from '@react-navigation/native';
import { BottomSheetModal } from '@gorhom/bottom-sheet';
import MaterialCommunityIcons from '@react-native-vector-icons/material-design-icons/static';
import Ionicons from '@react-native-vector-icons/ionicons/static';
import Feather from '@react-native-vector-icons/feather/static';
import FontAwesome6 from '@react-native-vector-icons/fontawesome6/static';
import FastImage from '@d11/react-native-fast-image';

import ProductItem from './Product.web';
import ProductListNative from './ProductListNative';
import { AlphaRail, AlphaRailHandle } from './AlphaRail';
import AppSearchBarWeb from '../../../components/Reusables/AppSearchBar.web';
import searchContext from '../../../Context/SearchContext';
import useAxiosPrivate from '../../../hooks/useAxiosPrivate';
import useAuth from '../../../hooks/useAuth';
import useLocation from '../../../hooks/useLocation';
import useFetchSubCategories from '../../../hooks/useFetchSubCategories';
import useKeyboardAwareListScroll from '../../../hooks/useKeyboardAwareListScroll';
import { showToast } from '../../../services/toast';
import { ExtrasContext } from '../../../Context/ExtrasContext';
import ChatwootService from '../../../services/ChatwootService';
import { requestNotificationsOnUserGesture } from '../../../services/requestNotificationsOnUserGesture';
import useChatwootSnapshot from '../../../hooks/useChatwootSnapshot';
import SupportUnreadBadge from '../../../components/support/SupportUnreadBadge';
import ErrorContext from '../../../Context/ErrorContext';
import { ModalContext } from '../../../Context/modalContext';
import { useNavigation as useNavigationContext } from '../../../Context/NavigationContext';
import { axiosPrivate } from '../../../axiosClient';
import useBackofficeOwnerAuth from '../../../hooks/useBackofficeOwnerAuth';

import {
  addupdateProducts,
  clearProductsBySubCategory,
  resetSearchProducts,
  seedProductsBySubCategoryFromDocs,
  seedSearchProductsFromLocal,
  selectProducts,
  selectProductsBySubCategory,
  selectProductsByTabKeyMap,
  selectSearchedProducts,
  selectSubCategories,
  updateProductsBySubCategory,
  updateSearchProducts,
  type SearchedProductsObject,
} from '../../../redux/productSlice';
import {
  getLocationEpoch,
  isCurrentLocationEpoch,
} from '../../../utils/locationEpoch';

import {
  ClearCart,
  clearCartOrderData,
  DecreaseCart,
  setUpdatingCartitems,
  selectCartTotalQuantity,
} from '../../../redux/cartSlice';
import { getTotals } from '../../../redux/cartSlice';
import { clearOrderDetails } from '../../../redux/orderDetailsSlice';
import { resetCheckoutPaymentIntent } from '../../../redux/checkoutSlice';

import {
  getProducts,
  getSearchedProducts,
  getProductsBySubCategory as apiGetProductsBySubCategory,
} from '../../../api/products';
import { PRODUCT_LIMIT, FEATURE_FLAGS } from '../../../CONSTANTS';
import {
  fs,
  s,
  v,
  iconSize,
  borderWidth,
  dumpTokens,
  forceHttps,
} from '../../../utils/libs';
import { scrollToProductOrTop } from '../../../utils/productListScroll';
import Typography from '../../../components/Reusables/Typography';
import CustomBottomSheet from '../../../components/Reusables/bottomSheet/CustomBottomSheet';
import Button from '../../../components/Reusables/Button';
import ClearBasketActions from '../../../components/sale/ClearBasketActions';
import ProductDetailBottomSheet from '../../../components/ProductDetailBottomSheet';
import FilterModal from '../../../components/Modals/FilterModal';
import CalculatorModal from '../../../components/Modals/CalculatorModal';
import {
  SettingsDetails,
  addSingleSetting,
  selectProductCard,
  selectProductCardLayout,
  selectShowProductImages,
  selectShowAlphaRail,
} from '../../../redux/SettingsSlice';
import { useTheme, GTFonts, withOpacity, useLayoutSize } from '../../../theme';
import {
  selectProductSort,
  type ProductSortOption,
} from '../../../redux/productSortSlice';
import { useAppSelector } from '../../../redux/helper';
import { selectUnseenNotificationsCount } from '../../../redux/notificationsSlice';
import AppSkeleton from '../../../components/Reusables/AppSkeleton';
import WebHeaderBar from '../../../components/web/WebHeaderBar';
import useWebHeader from '../../../components/web/useWebHeader';
import HeaderIconButton from '../../../components/Reusables/headers/HeaderIconButton';
import SaleCustomerBanner from '../../../components/sale/SaleCustomerBanner';
import {
  getProductGridNumColumns,
  getProductRowNumColumns,
  shouldShowTabletSidebarLayoutSetting,
} from '../../../utils/layoutUtils';
import { PRODUCT_LIST_SEARCH_LIMIT } from '../../../CONSTANTS';
import {
  getEffectiveQuantity,
  getEffectiveSellingPrice,
  getPriceRangeDisplay,
  isVariantProduct,
} from '../../../utils/variantHelpers';
import { getProductKind } from '../../../utils/productKind';

const throttle = (func: Function, limit: number) => {
  let inThrottle: boolean;
  return function (this: any, ...args: any[]) {
    const context = this;
    if (!inThrottle) {
      func.apply(context, args);
      inThrottle = true;
      setTimeout(() => (inThrottle = false), limit);
    }
  };
};

// Stable empty identity so the originalOrderIds selector returns the SAME
// reference outside edit mode. Prevents spurious re-renders.
const EMPTY_IDS: string[] = [];

const ProductList = () => {
  const { width, height, useSidebarNav } = useLayoutSize();
  const showTabletSidebarLayoutSetting = shouldShowTabletSidebarLayoutSetting(
    width,
    height,
  );
  const isPortraitSidebarMode =
    showTabletSidebarLayoutSetting && useSidebarNav;
  const insets = useSafeAreaInsets();

  // Use native layout when not in sidebar mode (web small, or native phone/portrait)
  if (!useSidebarNav) {
    return <ProductListNative />;
  }

  const navigation = useNavigation();
  // @ts-ignore
  const route = useRoute();

  const lastProductBottomSheetModalRef = useRef<BottomSheetModal>(null as any);
  const productDetailBottomSheetRef = useRef<BottomSheetModal>(null as any);
  const subcategoryPickerSheetRef = useRef<BottomSheetModal>(null as any);

  const ref = useRef(true);
  const subcategoryFlatListRef = useRef<FlashListRef<any>>(null);
  const productsFlatlistRef = useRef<FlashListRef<any>>(null);
  const subcategoryHeaderListRef = useRef<FlatList<string>>(null);
  const searchInputRef = useRef<TextInput>(null);

  // Stable viewability config for FlashList (avoids recreating on each render)
  const viewabilityConfig = useRef({ itemVisiblePercentThreshold: 60 }).current;

  const { clearOrderDetailsData } = useNavigationContext();
  const dispatch = useDispatch();
  const axiosprivate = useAxiosPrivate();
  const { auth, setAuth } = useAuth();
  const { autoLoginOwner } = useBackofficeOwnerAuth();
  const { isRowLayout } = useContext(ExtrasContext);
  const { search, setSearch } = useContext(searchContext);
  const { networkAvailableGlobal } = useContext(ExtrasContext);
  const { handleError, resetSnackMessage } = useContext(ErrorContext);
  const {
    setFilterModal,
    setInput,
    setSelectedCustomerId,
    setAssignedStaffId,
  } = useContext(ModalContext);
  const { theme } = useTheme();
  const styles = useMemo(
    () => createStyles(theme, width, isPortraitSidebarMode),
    [isPortraitSidebarMode, theme, width],
  );

  const settings = useSelector(SettingsDetails);
  const showProductImages = useAppSelector(selectShowProductImages);
  const showAlphaRailPref = useAppSelector(selectShowAlphaRail);
  const productCard = useAppSelector(selectProductCard);
  const productCardLayout = useAppSelector(selectProductCardLayout);
  const sortOption = useSelector(selectProductSort);
  const cartTotalQuantity = useAppSelector(selectCartTotalQuantity);

  // IDs of products from the order being edited (pinned at top).
  // Uses stable EMPTY_IDS to avoid re-renders when not in edit mode.
  const originalOrderIds = useSelector((state: any) => {
    if (!state?.cart?.updatingCartitems) return EMPTY_IDS;
    const order = state?.cart?.cartOrderData?.order;
    if (!order?.length) return EMPTY_IDS;
    const ids: string[] = [];
    for (const o of order) {
      const id = o?.bp_product_information?._id;
      if (id) ids.push(id);
    }
    return ids.length ? ids : EMPTY_IDS;
  }, shallowEqual);

  // A-Z rail is only shown when sort is alphabetical
  const isAlphaSort = sortOption === 'name_asc' || sortOption === 'default';
  const alphaRailEnabled =
    FEATURE_FLAGS.ALPHA_RAIL && showAlphaRailPref && isAlphaSort;

  // Refs for AlphaRail imperative API
  const alphaRailRef = useRef<AlphaRailHandle>(null);

  const employeedata = useSelector(
    (state: any) => state.auth.inApp.account.employeeInfo,
  );
  const { isAdmin } = useLocation();
  const fetchSubCategories = useFetchSubCategories();

  const subcategories = useSelector(selectSubCategories);
  const searchedproducts = useSelector(selectSearchedProducts);
  const productsbysubcategory = useSelector(selectProductsBySubCategory);
  const products = useSelector(selectProducts);
  const productsByTabKeyMap = useAppSelector(selectProductsByTabKeyMap);

  const [loading, setLoading] = useState(false);
  const [refresh, setRefresh] = useState(false);
  const [categoryloading, setCategoryLoading] = useState(false);
  const [subcategory, setSubcategory] = useState('AllProducts');
  const [subcategorydata, setSubcategoryData] = useState(
    productsbysubcategory.docs ?? [],
  );
  const [allSubCategories, setAllSubCategories] = useState<string[]>([]);
  const [searchdata, setSearchData] = useState(searchedproducts.data ?? []);
  const [maindata, setMaindata] = useState(products.docs ?? []);
  const [searching, setSearching] = useState(false);
  const [localSearch, setLocalSearch] = useState(search || '');
  const productsOffsetRef = useRef(0);
  const subCategoryProductsOffsetRef = useRef(0);
  const loadingRef = useRef(false);
  const activeSubcategoryRef = useRef<string>(subcategory);
  const [tempProduct, setTempProduct] = useState<any>(null);
  const [selectedProduct, setSelectedProduct] = useState<any>(null);
  const [subcategoryPickerOpen, setSubcategoryPickerOpen] = useState(false);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [isRefreshing, setIsRefreshing] = useState(false);
  // A–Z is the new default; 'default' is legacy and also treated as A–Z.
  const hasActiveSort = sortOption !== 'name_asc' && sortOption !== 'default';
  const [calculatorOpen, setCalculatorOpen] = useState(false);
  const chatwootSnapshot = useChatwootSnapshot();
  const unreadCount = useAppSelector(selectUnseenNotificationsCount);
  const notificationIconName =
    unreadCount > 0 ? 'notifications' : 'notifications-outline';
  const notificationColor = unreadCount > 0 ? '#FF9800' : theme.colors.textInverse;
  const supportColor = chatwootSnapshot.unread.hasUnread
    ? '#FF9800'
    : theme.colors.textInverse;

  useEffect(() => {
    activeSubcategoryRef.current = subcategory;
  }, [subcategory]);

  useFocusEffect(
    useCallback(() => {
      // @ts-ignore
      if (route && route?.params?.from) {
        setSubcategory('AllProducts');
        const params = route.params as { from?: string; productId?: string };
        const from = params.from;
        if (
          from === 'Checkout' ||
          from === 'NewProduct' ||
          from === 'OrderDetails'
        ) {
          // Set by VIEW on Add Product's success message. Clearing `from` with
          // it keeps the re-run this triggers from scrolling back to the top.
          const productId = params.productId;
          if (productId) {
            (navigation as any).setParams({
              from: undefined,
              productId: undefined,
            });
          }
          setTimeout(() => {
            scrollToProductOrTop(
              productsFlatlistRef.current,
              currentDataRef.current,
              productId,
            );
          }, 150);
        }
      }
      return () => {
        resetSnackMessage?.();
      };
    }, [route, navigation, resetSnackMessage]),
  );

  useEffect(() => {
    if (products?.docs?.length > 0) setMaindata(products.docs);
  }, [products?.docs]);

  useEffect(() => {
    if (subcategories) {
      const all = ['AllProducts'];
      all.push(...(subcategories as any));
      setAllSubCategories(all);
    }
  }, [subcategories]);

  useEffect(() => {
    if (searchedproducts?.data) setSearchData(searchedproducts.data);
  }, [searchedproducts]);

  const getAllproducts = useCallback(
    async (currentpage: number, limit: number): Promise<boolean> => {
      const epoch = getLocationEpoch();
      try {
        if (loadingRef.current) return false;
        loadingRef.current = true;
        setLoading(true);
        try {
          console.log('getAllproductsStarted2');
          const response = await getProducts(
            axiosprivate,
            currentpage,
            limit,
            auth,
          );
          if (!isCurrentLocationEpoch(epoch)) return false;
          if (response?.data?.status === 200) {
            dispatch(addupdateProducts(response?.data.data));
            return true;
          }
          if (response?.data?.message === 'Employee not found') {
            setAuth({ accessToken: '', refreshToken: '' });
            dumpTokens();
            return false;
          }
          handleError?.(response?.data?.message, 'error', true);
          return false;
        } catch (error: any) {
          if (!isCurrentLocationEpoch(epoch)) return false;
          handleError?.(error?.message, 'error', true);
          return false;
        } finally {
          loadingRef.current = false;
          setLoading(false);
        }
      } catch (error: any) {
        handleError?.(error?.message, 'error', true);
        return false;
      }
    },
    [auth, axiosprivate, dispatch, handleError, setAuth],
  );

  const getSearchedproducts = async (currentpage: number) => {
    if (loading) return;
    setSearching(true);
    try {
      const response = await getSearchedProducts(
        axiosPrivate,
        search,
        currentpage,
        PRODUCT_LIST_SEARCH_LIMIT,
        auth,
      );
      const searchedProductsObject: SearchedProductsObject =
        response?.data?.data;
      if (response?.data?.status === 200) {
        dispatch(updateSearchProducts(searchedProductsObject));
      } else {
        handleError?.(response?.data?.message, 'error', true);
      }
    } catch (error: any) {
      handleError?.(error?.message, 'error', true);
    } finally {
      setSearching(false);
    }
  };

  const throttledGetSearchedproducts = useMemo(
    () => throttle(getSearchedproducts, 300),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [search, auth],
  );

  useEffect(() => {
    const term = search?.trim() || '';
    if (term.length > 1) {
      dispatch(seedSearchProductsFromLocal(term));
      throttledGetSearchedproducts(1);
    } else if (term.length < 1) {
      dispatch(resetSearchProducts());
      setSearchData([]);
    }
  }, [search, dispatch, throttledGetSearchedproducts]);

  const getProductsBySubCategory = useCallback(
    async (currentpage: number, subcat?: string): Promise<boolean> => {
      const requestedSubcat = String(subcat || '');
      if (!requestedSubcat || requestedSubcat === 'AllProducts') return false;
      const limit = 20;
      const epoch = getLocationEpoch();
      if (currentpage === 1) setCategoryLoading(true);
      try {
        const response = await apiGetProductsBySubCategory(
          axiosprivate,
          requestedSubcat,
          currentpage,
          limit,
          auth,
        );
        if (!isCurrentLocationEpoch(epoch)) return false;
        if (activeSubcategoryRef.current !== requestedSubcat) return false;
        if (response?.data?.status === 200) {
          dispatch(updateProductsBySubCategory(response?.data.data));
          return true;
        }
        handleError?.(response?.data?.message, 'error', true);
        return false;
      } catch (error: any) {
        handleError?.(error?.message, 'error', true);
        return false;
      } finally {
        if (
          currentpage === 1 &&
          activeSubcategoryRef.current === requestedSubcat
        ) {
          setCategoryLoading(false);
        }
      }
    },
    [dispatch, auth, axiosprivate, handleError],
  );

  const fetchProductsBySubCategory = useCallback(
    async (subcat: string) => {
      setSubcategory(subcat);
      if (subcat && subcat !== 'AllProducts') {
        setCategoryLoading(true);
        dispatch(clearProductsBySubCategory());
        const key = String(subcat || '').trim();
        const seededDocs = productsByTabKeyMap.get(key) ?? [];
        dispatch(seedProductsBySubCategoryFromDocs(seededDocs));
        void getProductsBySubCategory(1, subcat);
      }
    },
    [dispatch, getProductsBySubCategory, productsByTabKeyMap],
  );

  useEffect(() => {
    if (subcategory && subcategory !== 'AllProducts') {
      setSubcategoryData(productsbysubcategory.docs);
      subcategoryFlatListRef.current?.scrollToOffset({
        animated: false,
        offset: subCategoryProductsOffsetRef.current,
      });
    } else if (subcategory === 'AllProducts') {
      productsFlatlistRef.current?.scrollToOffset({
        animated: false,
        offset: productsOffsetRef.current,
      });
    }
  }, [subcategory, productsbysubcategory.docs]);

  const loadMoreProducts = useCallback(() => {
    if (products.hasNextPage) getAllproducts(products.nextPage, PRODUCT_LIMIT);
  }, [products.hasNextPage, products.nextPage, getAllproducts]);

  const loadMoreSearchResults = useCallback(() => {
    const term = search?.trim() || '';
    if (
      term.length > 0 &&
      searchedproducts.hasNextPage &&
      searchedproducts.nextPage
    ) {
      getSearchedproducts(searchedproducts.nextPage);
    }
  }, [searchedproducts, search]);

  const loadMoreProductsFromSubCategory = useCallback(() => {
    if (
      subcategory &&
      subcategory !== 'AllProducts' &&
      productsbysubcategory.hasNextPage &&
      productsbysubcategory.nextPage
    ) {
      getProductsBySubCategory(productsbysubcategory.nextPage, subcategory);
    }
  }, [
    subcategory,
    productsbysubcategory.hasNextPage,
    productsbysubcategory.nextPage,
    getProductsBySubCategory,
  ]);

  const refetchProductData = useCallback(async (): Promise<boolean> => {
    const [subcategoriesOk, productsOk] = await Promise.all([
      fetchSubCategories(),
      subcategory === 'AllProducts'
        ? getAllproducts(1, PRODUCT_LIMIT)
        : getProductsBySubCategory(1, subcategory),
    ]);
    return subcategoriesOk && productsOk;
  }, [
    subcategory,
    fetchSubCategories,
    getAllproducts,
    getProductsBySubCategory,
  ]);

  const Refresh = useCallback(async () => {
    setRefresh(true);
    try {
      const ok = await refetchProductData();
      if (ok) {
        showToast('Products refreshed', 'success');
      }
    } finally {
      setRefresh(false);
    }
  }, [refetchProductData]);

  // Manual refresh handler with visual feedback
  const handleManualRefresh = useCallback(async () => {
    setIsRefreshing(true);
    try {
      const ok = await refetchProductData();
      if (ok) {
        showToast('Products refreshed', 'success');
      }
    } finally {
      setTimeout(() => setIsRefreshing(false), 500);
    }
  }, [refetchProductData]);

  const handleInternetStatusPress = () => {
    if (Platform.OS !== 'android') return;
    ToastAndroid.show(
      networkAvailableGlobal
        ? 'You have a stable internet connection'
        : 'You are currently offline. Please check your internet connection and try again.',
      ToastAndroid.SHORT,
    );
  };

  const handleScroll = (event: NativeSyntheticEvent<NativeScrollEvent>) => {
    productsOffsetRef.current = event.nativeEvent.contentOffset.y;
  };
  const handleSubCategoryScroll = (
    event: NativeSyntheticEvent<NativeScrollEvent>,
  ) => {
    subCategoryProductsOffsetRef.current = event.nativeEvent.contentOffset.y;
  };

  const handleSubproductQuantity = useCallback(
    (product: any) => {
      dispatch(DecreaseCart(product));
      dispatch(getTotals());
    },
    [dispatch],
  );

  const showbottomSheetFn = useCallback(
    (product: any) => {
      if (settings?.showIncompleteOrderPopup !== false) {
        lastProductBottomSheetModalRef.current?.present();
        setTempProduct(product);
      } else {
        setTempProduct(product);
      }
    },
    [settings?.showIncompleteOrderPopup],
  );

  const handleProductPress = useCallback((product: any) => {
    setSelectedProduct(product);
    requestAnimationFrame(() => productDetailBottomSheetRef.current?.present());
  }, []);

  // Resolves the list that is currently rendered. Identity-stable (reads the
  // active tab from a ref) so cards don't re-render on subcategory changes.
  const getKeyboardScrollTarget = useCallback(() => {
    const isSubcategoryList =
      !!activeSubcategoryRef.current &&
      activeSubcategoryRef.current !== 'AllProducts';
    return isSubcategoryList
      ? {
          key: 'subcategory',
          listRef: subcategoryFlatListRef,
          offsetRef: subCategoryProductsOffsetRef,
        }
      : {
          key: 'all',
          listRef: productsFlatlistRef,
          offsetRef: productsOffsetRef,
        };
  }, []);

  // Lifts a focused product card above the on-screen keyboard (touch web), and
  // only by as much as the keyboard actually covers it.
  const {
    onInputFocus: handleCardInputFocus,
    onScrollBeginDrag: handleKeyboardScrollBeginDrag,
  } = useKeyboardAwareListScroll(getKeyboardScrollTarget);

  const closeModal = useCallback(() => {
    lastProductBottomSheetModalRef.current?.dismiss();
  }, []);

  const clearCart = useCallback(() => {
    handleSubproductQuantity(tempProduct);
    dispatch(ClearCart());
    dispatch(clearCartOrderData());
    dispatch(setUpdatingCartitems(false));
    dispatch(clearOrderDetails());
    dispatch(resetCheckoutPaymentIntent());
    setInput('');
    setSelectedCustomerId(null);
    setAssignedStaffId(null);
    closeModal();
    clearOrderDetailsData();
  }, [
    dispatch,
    tempProduct,
    clearOrderDetailsData,
    handleSubproductQuantity,
    closeModal,
    setInput,
    setSelectedCustomerId,
    setAssignedStaffId,
  ]);
  const numColumns = useMemo(() => {
    const contentWidth = useSidebarNav
      ? Math.max(width - s(WEB_NAV_SIDEBAR_RAIL_S), width * 0.5)
      : width;
    return isRowLayout
      ? getProductRowNumColumns(contentWidth)
      : getProductGridNumColumns(contentWidth);
  }, [width, isRowLayout, useSidebarNav]);

  const currentData = useMemo(() => {
    const base =
      search?.length > 1
        ? searchdata
        : subcategory !== 'AllProducts'
          ? subcategorydata
          : maindata;

    const sorted = [...base];
    switch (sortOption as ProductSortOption) {
      // 'default' maps to A–Z (legacy persisted value).
      case 'default':
      case 'name_asc':
        sorted.sort((a: any, b: any) =>
          String(a.bp_product_name).localeCompare(String(b.bp_product_name)),
        );
        break;
      case 'price_desc':
        sorted.sort(
          (a: any, b: any) =>
            (b.bp_product_selling_price ?? 0) -
            (a.bp_product_selling_price ?? 0),
        );
        break;
      case 'recent':
        sorted.sort((a: any, b: any) =>
          String(b._id).localeCompare(String(a._id)),
        );
        break;
      case 'in_stock':
        sorted.sort(
          (a: any, b: any) =>
            getEffectiveQuantity(b) - getEffectiveQuantity(a),
        );
        break;
      default:
        break;
    }

    // Pin only products from the original order snapshot during the edit flow.
    // Newly added products stay in their normal sorted position.
    if (originalOrderIds.length === 0) return sorted;
    const pinnedSet = new Set(originalOrderIds);
    const pinned: any[] = [];
    const rest: any[] = [];
    for (const product of sorted) {
      if (pinnedSet.has(product._id)) pinned.push(product);
      else rest.push(product);
    }
    return pinned.length ? [...pinned, ...rest] : sorted;
  }, [
    search,
    searchdata,
    subcategory,
    subcategorydata,
    maindata,
    sortOption,
    originalOrderIds,
  ]);

  const isInitialLoading =
    (loading || searching || categoryloading) && currentData.length === 0;
  const isLoadingMore =
    (loading || searching || categoryloading) && currentData.length > 0;

  // Refs for stable access in callbacks (avoid recreating callbacks)
  const currentDataRef = useRef(currentData);
  useEffect(() => {
    currentDataRef.current = currentData;
  }, [currentData]);

  const alphaRailEnabledRef = useRef(alphaRailEnabled);
  useEffect(() => {
    alphaRailEnabledRef.current = alphaRailEnabled;
  }, [alphaRailEnabled]);

  const originalOrderIdsRef = useRef<string[]>(originalOrderIds);
  useEffect(() => {
    originalOrderIdsRef.current = originalOrderIds;
  }, [originalOrderIds]);

  // A-Z rail: compute first index per letter for jump navigation
  const { letters, letterIndex } = useMemo(() => {
    if (
      !alphaRailEnabled ||
      !Array.isArray(currentData) ||
      currentData.length === 0
    ) {
      return {
        letters: [] as string[],
        letterIndex: {} as Record<string, number>,
      };
    }
    const idx: Record<string, number> = {};
    // Pinned original-order products go to '#' bucket
    const pinnedSet =
      originalOrderIds.length > 0 ? new Set(originalOrderIds) : null;
    for (let i = 0; i < currentData.length; i++) {
      const p = currentData[i];
      let key: string;
      if (pinnedSet?.has(p?._id)) {
        key = '#';
      } else {
        const raw = String(p?.bp_product_name || ' ')
          .trim()
          .charAt(0)
          .toLocaleUpperCase();
        key = /[A-Z]/.test(raw) ? raw : '#';
      }
      if (!(key in idx)) idx[key] = i;
    }
    const keys = Object.keys(idx).sort((a, b) => a.localeCompare(b));
    return { letters: keys, letterIndex: idx };
  }, [currentData, alphaRailEnabled, originalOrderIds]);

  // Scroll to letter when tapped on rail
  const scrollToLetter = useCallback(
    (letter: string) => {
      const firstIdx = letterIndex[letter];
      if (firstIdx == null) return;
      const listRef =
        subcategory !== 'AllProducts'
          ? subcategoryFlatListRef
          : productsFlatlistRef;
      // Pin the rail to the tapped letter BEFORE kicking off the scroll.
      // While pinned, setDominant is ignored, so passing-through letters
      // cannot flash highlighted during the jump. The pin is released when
      // the list comes to rest (onMomentumScrollEnd / onScrollEndDrag).
      alphaRailRef.current?.lock(letter);
      // FlashList scrollToIndex uses the data-array index (each product is
      // one item even with numColumns > 1).
      void listRef.current?.scrollToIndex({
        index: firstIdx,
        animated: true,
        viewPosition: 0,
      });
    },
    [letterIndex, subcategory],
  );

  // Release lock when scroll ends
  const handleAlphaRailUnlock = useCallback(() => {
    alphaRailRef.current?.unlock();
  }, []);

  // Compute category counts
  const categoryCounts = useMemo(() => {
    const map = new Map<string, number>();
    const docs = products?.docs ?? [];
    for (const p of docs as any[]) {
      const keys = new Set<string>();
      const cat = p?.bp_product_category;
      const sub = p?.bp_product_sub_category;
      if (cat) keys.add(cat);
      if (sub) keys.add(sub);
      for (const key of keys) {
        map.set(key, (map.get(key) || 0) + 1);
      }
    }
    const allCount = Number(products?.totalDocs ?? 0) || docs.length;
    return { map, allCount };
  }, [products?.docs, products?.totalDocs]);

  // Callback for FlatList - uses refs for stable access
  const onViewableItemsChanged = useRef(({ viewableItems }: any) => {
    try {
      if (!Array.isArray(viewableItems) || viewableItems.length === 0) return;
      const data = currentDataRef.current ?? [];
      const firstIndex = viewableItems[0]?.index ?? 0;
      const start = Math.max(0, firstIndex);
      const end = Math.min(data.length, start + 40);
      const toPreload = data.slice(start, end);
      const sources = toPreload
        .map((p: any) => String(p?.bp_product_image || ''))
        .filter(u => !!u)
        .map(uri => ({
          uri: forceHttps(uri),
          priority: FastImage.priority.high,
        }));
      if (sources.length) FastImage.preload(sources as any);

      // Dominant-letter tracking for A-Z rail
      if (!alphaRailEnabledRef.current) return;
      const counts: Record<string, number> = {};
      const pinnedIds = originalOrderIdsRef.current;
      const pinnedSet = pinnedIds.length ? new Set(pinnedIds) : null;
      for (const vi of viewableItems) {
        const product = vi?.item;
        if (!product) continue;
        let key: string;
        if (pinnedSet?.has(product._id)) {
          key = '#';
        } else {
          const raw = String(product?.bp_product_name || ' ')
            .trim()
            .charAt(0)
            .toLocaleUpperCase();
          key = /[A-Z]/.test(raw) ? raw : '#';
        }
        counts[key] = (counts[key] || 0) + 1;
      }
      // Find most common letter in view
      const bucketKeys = Object.keys(counts).sort((a, b) => a.localeCompare(b));
      let best = '';
      let bestCount = -1;
      for (const k of bucketKeys) {
        if (counts[k] > bestCount) {
          bestCount = counts[k];
          best = k;
        }
      }
      if (best) alphaRailRef.current?.setDominant(best);
    } catch {}
  }).current;

  useEffect(() => {
    const initial = currentData.slice(0, 60);
    const sources = initial
      .map((p: any) => String(p?.bp_product_image || ''))
      .filter(u => !!u)
      .map(uri => ({
        uri: forceHttps(uri),
        priority: FastImage.priority.high,
      }));
    if (sources.length) FastImage.preload(sources as any);
  }, [currentData]);

  const onEndReachedHandler = useMemo(() => {
    const term = search?.trim() || '';
    return term.length > 0
      ? loadMoreSearchResults
      : subcategory !== 'AllProducts'
        ? loadMoreProductsFromSubCategory
        : loadMoreProducts;
  }, [
    search,
    subcategory,
    loadMoreSearchResults,
    loadMoreProductsFromSubCategory,
    loadMoreProducts,
  ]);

  // Navigate to new product screen (admin only)
  const navigateToNewProduct = useCallback(() => {
    (navigation as any).navigate('NewProduct');
  }, [navigation]);

  // Handle search submit
  const handleSearchSubmit = useCallback(() => {
    if (setSearch) {
      setSearch(localSearch);
    }
  }, [localSearch, setSearch]);

  // Handle search change
  const handleSearchChange = useCallback(
    (text: string) => {
      setLocalSearch(text);
      if (setSearch) {
        setSearch(text);
      }
    },
    [setSearch],
  );

  // Clear search
  const handleClearSearch = useCallback(() => {
    setLocalSearch('');
    if (setSearch) {
      setSearch('');
    }
    searchInputRef.current?.focus();
  }, [setSearch]);

  const renderProductItem = useCallback(
    ({ item: product, index }: { item: any; index: number }) => {
      const hasVariants = isVariantProduct(product);
      const variantCount = Array.isArray(product?.bp_product_variants)
        ? product.bp_product_variants.length
        : 0;
      const effectivePrice = getEffectiveSellingPrice(product) ?? 0;
      const effectiveQuantity = getEffectiveQuantity(product);
      const priceRangeText = hasVariants ? getPriceRangeDisplay(product) : undefined;
      return (
        <View
          style={[
            styles.productItemWrapper,
            // FlashList handles column layout internally — use flex sizing, not % width
            // numColumns > 1 && { flex: 1, flexBasis: 0 },
          ]}>
          <ProductItem
            showbottomSheetFn={showbottomSheetFn}
            handleSubproductQuantity={handleSubproductQuantity}
            category={product.bp_product_category}
            itemname={product.bp_product_name}
            price={effectivePrice}
            quantity={effectiveQuantity}
            mass={product.bp_product_mass}
            unitofmass={product.bp_product_unit_of_mass}
            productID={product._id}
            hasVariants={hasVariants}
            variantCount={variantCount}
            variants={product.bp_product_variants ?? []}
            variantAttributes={product.bp_product_variant_attributes ?? []}
            priceRangeText={priceRangeText}
            model={product.bp_product_model}
            size={product.bp_product_size}
            msl={product?.bp_minimum_stock_level}
            price_type={product.bp_product_price_type}
            kind={getProductKind(product)}
            index={index}
            image={product.bp_product_image}
            isFavourite={product.bp_product_is_favourite}
            onProductPress={handleProductPress}
            onMissingImagePress={(p: any) =>
              (navigation as any).navigate('EmployeeAddProductImage', {
                productId: p?.productID || product._id,
              })
            }
            onCardInputFocus={handleCardInputFocus}
          />
        </View>
      );
    },
    [
      numColumns,
      showbottomSheetFn,
      handleSubproductQuantity,
      handleProductPress,
      handleCardInputFocus,
      navigation,
      styles,
    ],
  );

  const renderCategoryPill = useCallback(
    ({ item }: { item: string }) => {
      const isActive = subcategory === item;
      const label = item === 'AllProducts' ? 'All' : item;
      const count =
        item === 'AllProducts'
          ? categoryCounts.allCount
          : categoryCounts.map.get(item) || 0;

      return (
        <Pressable
          onPress={() => fetchProductsBySubCategory(item)}
          style={({ hovered }: any) => [
            styles.categoryPill,
            isActive && styles.categoryPillActive,
            hovered && !isActive && styles.categoryPillHovered,
          ]}>
          <Text
            style={[
              styles.categoryLabel,
              isActive && styles.categoryLabelActive,
            ]}>
            {label}
          </Text>
          <View
            style={[
              styles.categoryCountBadge,
              isActive && styles.categoryCountBadgeActive,
            ]}>
            <Text
              style={[
                styles.categoryCount,
                isActive && styles.categoryCountActive,
              ]}>
              {count}
            </Text>
          </View>
        </Pressable>
      );
    },
    [subcategory, fetchProductsBySubCategory, styles, categoryCounts],
  );

  const keyExtractor = useCallback((item: any) => item._id, []);
  const keyTopExtractor = useCallback(
    (item: string, i: number) => `${item}-${i}`,
    [],
  );

  // Render footer with "Add More Products" card for admins
  const renderFooter = useCallback(() => {
    if (isInitialLoading) return null;
    if (isLoadingMore) {
      return (
        <View style={styles.skeletonFooter}>
          <AppSkeleton
            mode="product_catalog"
            count={Math.max(2, numColumns * 2)}
            numColumns={numColumns}
            isRowLayout={false}
            showProductImages={showProductImages}
          />
        </View>
      );
    }

    const term = (search ?? '').trim();
    const isSearchMode = term.length > 1;
    const isSubcategoryMode = !isSearchMode && subcategory !== 'AllProducts';

    const inventoryCount =
      Number(products?.totalDocs ?? 0) || (products?.docs?.length ?? 0);
    const inventoryEmpty = inventoryCount === 0;
    const viewEmpty = currentData.length <= 0;

    const noSearchResults = isSearchMode && viewEmpty && !inventoryEmpty;
    const subcategoryEmpty = isSubcategoryMode && viewEmpty && !inventoryEmpty;

    if (viewEmpty && isAdmin) {
      const title = noSearchResults
        ? `No results for "${term}"`
        : subcategoryEmpty
          ? `No products in "${subcategory}"`
          : 'No products yet';

      const subtitle = noSearchResults
        ? 'Not found in inventory. Create it as a new product?'
        : subcategoryEmpty
          ? 'This category is empty. Add a product to this category.'
          : 'Add products to get started';

      const ctaText = noSearchResults ? `Add "${term}"` : 'Add Product';

      return (
        <View style={styles.emptyAdminContainer}>
          <MaterialCommunityIcons
            name="package-variant"
            size={iconSize(64)}
            color={theme.colors.textTertiary}
          />
          <Text style={styles.emptyText}>{title}</Text>
          <Text style={styles.emptySubtext}>{subtitle}</Text>
          <Button
            backgroundColor={theme.colors.success}
            rippleColor={theme.colors.success}
            text={ctaText}
            fontFamily={GTFonts.regular}
            icon=""
            height={44}
            paddingHorizontal={44}
            borderRadius={5}
            onPress={navigateToNewProduct}
          />
        </View>
      );
    }

    if (isAdmin && currentData.length > 0) {
      return (
        <View style={styles.footerAddCardWrapper}>
          <Pressable
            onPress={navigateToNewProduct}
            style={({ hovered }: any) => [
              styles.addCard,
              hovered && styles.addCardHovered,
            ]}>
            <MaterialCommunityIcons
              name="plus-box-outline"
              size={iconSize(26)}
              color={theme.colors.primary}
              style={{ marginBottom: v(6) }}
            />
            <Text style={styles.addCardTitle}>Add More Products</Text>
            <Text style={styles.addCardSubtitle}>
              Create a new product
            </Text>
          </Pressable>
        </View>
      );
    }

    if (viewEmpty) {
      const subtitle = noSearchResults
        ? 'Try a different search term'
        : subcategoryEmpty
          ? `No products in "${subcategory}"`
          : 'Products will appear here';
      return (
        <View style={styles.emptyContainer}>
          <MaterialCommunityIcons
            name="package-variant"
            size={iconSize(64)}
            color={theme.colors.textTertiary}
          />
          <Text style={styles.emptyText}>No products found</Text>
          <Text style={styles.emptySubtext}>{subtitle}</Text>
        </View>
      );
    }
    return null;
  }, [
    isInitialLoading,
    isLoadingMore,
    currentData,
    isAdmin,
    search,
    subcategory,
    products?.totalDocs,
    products?.docs,
    styles,
    theme,
    navigateToNewProduct,
    numColumns,
    showProductImages,
  ]);

  useEffect(() => {
    if (subcategoryPickerOpen) subcategoryPickerSheetRef.current?.present();
    else subcategoryPickerSheetRef.current?.dismiss();
  }, [subcategoryPickerOpen]);

  // Live time update - every second
  useEffect(() => {
    setCurrentTime(new Date());
    const intervalId = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(intervalId);
  }, []);

  const renderPickerItem = useCallback(
    ({ item: name }: { item: string }) => {
      const isActive = name === subcategory;
      return (
        <Pressable
          onPress={() => {
            setSubcategoryPickerOpen(false);
            fetchProductsBySubCategory(name);
            const index = allSubCategories.findIndex(s => s === name);
            if (index >= 0) {
              requestAnimationFrame(() => {
                try {
                  subcategoryHeaderListRef.current?.scrollToIndex({
                    index,
                    animated: true,
                    viewPosition: 0.5,
                  });
                } catch {}
              });
            }
          }}
          style={[
            styles.pickerItem,
            isActive ? styles.pickerItemActive : null,
          ]}>
          <MaterialCommunityIcons
            name={isActive ? 'radiobox-marked' : 'radiobox-blank'}
            size={iconSize(18)}
            color={isActive ? theme.colors.success : theme.colors.text}
          />
          <Text
            style={[
              styles.pickerLabel,
              isActive ? styles.pickerLabelActive : null,
            ]}>
            {name === 'AllProducts' ? 'All' : name}
          </Text>
        </Pressable>
      );
    },
    [
      subcategory,
      allSubCategories,
      fetchProductsBySubCategory,
      styles,
      theme.colors.success,
      theme.colors.text,
    ],
  );

  const handleNavigateNotifications = useCallback(() => {
    void requestNotificationsOnUserGesture();
    const nav = navigation as any;
    const parentNavigation = nav.getParent?.();
    if (parentNavigation?.navigate) {
      parentNavigation.navigate('Notifications');
      return;
    }
    nav.navigate('Notifications');
  }, [navigation]);

  const handleToggleProductLayout = useCallback(() => {
    const next = productCardLayout === 'row' ? 'grid' : 'row';
    dispatch(
      addSingleSetting({
        productCard: { ...productCard, layout: next },
      }),
    );
  }, [dispatch, productCard, productCardLayout]);

  const handleOpenSupportChat = useCallback(() => {
    void ChatwootService.openChat();
  }, []);

  const handleHeaderBack = useCallback(() => {
    try {
      if ((navigation as any)?.canGoBack?.()) {
        (navigation as any).goBack();
        return;
      }
      const parent = (navigation as any)?.getParent?.();
      if (parent?.popTo) {
        parent.popTo('DrawerNavigation', { screen: 'Home' });
        return;
      }
      if (parent?.navigate) {
        parent.navigate('DrawerNavigation', { screen: 'Home' });
        return;
      }
      (navigation as any)?.navigate?.('DrawerNavigation', { screen: 'Home' });
    } catch {
      // ignore
    }
  }, [navigation]);

  const isFocused = useIsFocused();
  const webHeaderActive = useSidebarNav && isFocused;

  const headerNode = useMemo(() => {
    if (!webHeaderActive) return null;

    return (
      <WebHeaderBar
        onBack={handleHeaderBack}
        rightSlot={
          <View style={styles.topBarRight}>
            <HeaderIconButton
              tooltip="Sort products"
              onPress={() => setFilterModal(true)}
              style={({ hovered }: any) => [
                styles.topBarAction,
                hovered && styles.topBarActionHovered,
              ]}>
              <Ionicons
                name="options-outline"
                size={iconSize(22)}
                color={hasActiveSort ? theme.colors.warning : theme.colors.textInverse}
              />
            </HeaderIconButton>

            <HeaderIconButton
              tooltip="Refresh products"
              onPress={handleManualRefresh}
              disabled={isRefreshing}
              style={({ hovered }: any) => [
                styles.topBarAction,
                hovered && styles.topBarActionHovered,
              ]}>
              <MaterialCommunityIcons
                name="refresh"
                size={iconSize(22)}
                color={theme.colors.textInverse}
                style={isRefreshing ? { opacity: 0.5 } : undefined}
              />
            </HeaderIconButton>

            <HeaderIconButton
              tooltip={
                productCardLayout === 'row'
                  ? 'Switch to grid layout'
                  : 'Switch to list layout'
              }
              onPress={handleToggleProductLayout}
              style={({ hovered }: any) => [
                styles.topBarAction,
                hovered && styles.topBarActionHovered,
              ]}
              accessibilityRole="button"
              accessibilityLabel={
                productCardLayout === 'row'
                  ? 'Switch to grid layout'
                  : 'Switch to list layout'
              }>
              <MaterialCommunityIcons
                name={
                  productCardLayout === 'row' ? 'view-grid-outline' : 'view-list'
                }
                size={iconSize(22)}
                color={theme.colors.textInverse}
              />
            </HeaderIconButton>

            {!isPortraitSidebarMode ? (
              <Pressable
                style={[
                  styles.networkIndicator,
                  networkAvailableGlobal
                    ? styles.networkOnline
                    : styles.networkOffline,
                ]}>
                <Ionicons
                  name={networkAvailableGlobal ? 'wifi' : 'wifi-outline'}
                  size={iconSize(16)}
                  color={networkAvailableGlobal ? theme.colors.successLight : theme.colors.error}
                />
                <Text style={styles.networkText}>
                  {networkAvailableGlobal ? 'Online' : 'Offline'}
                </Text>
              </Pressable>
            ) : null}

            <View style={styles.dateTimeContainer}>
              <Feather
                name="calendar"
                size={iconSize(13)}
                color="rgba(255, 255, 255, 0.85)"
              />
              <Text style={styles.dateTimeText}>
                {currentTime.toLocaleDateString('en-US', {
                  weekday: 'short',
                  month: 'short',
                  day: 'numeric',
                  year: 'numeric',
                })}
              </Text>
            </View>
            <View style={styles.dateTimeContainer}>
              <Feather
                name="clock"
                size={iconSize(14)}
                color="rgba(255, 255, 255, 0.85)"
              />
              <Text style={[styles.dateTimeText, styles.timeText]}>
                {currentTime.toLocaleTimeString('en-US', {
                  hour: '2-digit',
                  minute: '2-digit',
                  second: '2-digit',
                })}
              </Text>
            </View>

            {isAdmin ? (
              <HeaderIconButton
                tooltip="Customer support"
                onPress={handleOpenSupportChat}
                style={({ hovered }: any) => [
                  styles.topBarAction,
                  hovered && styles.topBarActionHovered,
                ]}
                accessibilityRole="button"
                accessibilityLabel="Open customer support chat">
                <View style={styles.supportIconWrap}>
                  <Ionicons
                    name="chatbubbles-sharp"
                    size={iconSize(22)}
                    color={supportColor}
                  />
                  {chatwootSnapshot.unread.hasUnread ? (
                    <SupportUnreadBadge
                      count={chatwootSnapshot.unread.unreadCount}
                      size="small"
                    />
                  ) : null}
                </View>
              </HeaderIconButton>
            ) : null}

            <HeaderIconButton
              tooltip="Notifications"
              onPress={handleNavigateNotifications}
              style={({ hovered }: any) => [
                styles.topBarAction,
                hovered && styles.topBarActionHovered,
              ]}
              accessibilityRole="button"
              accessibilityLabel="Open notifications">
              <View>
                <Ionicons
                  name={notificationIconName}
                  size={iconSize(22)}
                  color={notificationColor}
                />
                {unreadCount > 0 ? <View style={styles.unreadDot} /> : null}
              </View>
            </HeaderIconButton>
          </View>
        }>
        <View style={styles.searchSlot}>
          <AppSearchBarWeb
            placeholder="Enter product name"
            value={localSearch}
            onChangeText={handleSearchChange}
            onSubmit={handleSearchSubmit}
            onClear={handleClearSearch}
            showLeadingIcon={true}
            showBarcodeScan={true}
            height={v(50)}
            width={
              isPortraitSidebarMode ? width * 0.46 : width * 0.32
            }
            onBarcodeScan={() => (navigation as any).navigate('ScanProduct')}
            inputRef={searchInputRef}
            containerStyle={styles.searchContainerExpanded}
          />
        </View>
      </WebHeaderBar>
    );
  }, [
    webHeaderActive,
    styles,
    localSearch,
    handleSearchChange,
    handleSearchSubmit,
    handleClearSearch,
    handleHeaderBack,
    navigation,
    setFilterModal,
    hasActiveSort,
    handleManualRefresh,
    isRefreshing,
    handleToggleProductLayout,
    productCardLayout,
    networkAvailableGlobal,
    currentTime,
    isAdmin,
    handleOpenSupportChat,
    handleNavigateNotifications,
    chatwootSnapshot.unread.hasUnread,
    chatwootSnapshot.unread.unreadCount,
    notificationIconName,
    notificationColor,
    unreadCount,
    isPortraitSidebarMode,
    theme.colors.textInverse,
  ]);

  useWebHeader(headerNode, webHeaderActive);

  return (
    <View style={styles.container}>
      {/* On web large, the header is rendered by WebSidebarLayout (spans above sidebar). */}
      {!useSidebarNav ? headerNode : null}

      {/* Category Pills */}
      <View style={styles.categoriesSection}>
        {/* Category List Toggle */}
        <Pressable
          onPress={() => setSubcategoryPickerOpen(true)}
          style={({ hovered }: any) => [
            styles.categoryToggle,
            hovered && styles.categoryToggleHovered,
          ]}>
          <Feather
            name="list"
            size={iconSize(18)}
            color={theme.colors.textSecondary}
          />
        </Pressable>

        <ScrollView
          horizontal
          showsHorizontalScrollIndicator={false}
          contentContainerStyle={styles.categoriesContent}>
          {allSubCategories.map((item, index) => (
            <React.Fragment key={`${item}-${index}`}>
              {renderCategoryPill({ item })}
            </React.Fragment>
          ))}
        </ScrollView>
      </View>

      <SaleCustomerBanner />

      {/* Products Grid */}
      <View style={styles.productsArea}>
        <View style={styles.productsListContainer}>
          <FlashList
            ref={
              subcategory !== 'AllProducts'
                ? subcategoryFlatListRef
                : productsFlatlistRef
            }
            keyExtractor={keyExtractor}
            onScroll={
              subcategory !== 'AllProducts'
                ? handleSubCategoryScroll
                : handleScroll
            }
            data={currentData}
            renderItem={renderProductItem}
            style={{ flex: 1 }}
            contentContainerStyle={styles.gridContent}
            numColumns={numColumns}
            key={numColumns}
            drawDistance={Platform.OS === 'web' ? undefined : 400}
            onEndReachedThreshold={0.6}
            bounces={true}
            onEndReached={onEndReachedHandler}
            onViewableItemsChanged={onViewableItemsChanged}
            viewabilityConfig={viewabilityConfig}
            refreshing={refresh}
            onRefresh={Refresh}
            showsVerticalScrollIndicator={!alphaRailEnabled}
            onMomentumScrollEnd={handleAlphaRailUnlock}
            onScrollBeginDrag={handleKeyboardScrollBeginDrag}
            onScrollEndDrag={handleAlphaRailUnlock}
            ListEmptyComponent={
              isInitialLoading ? (
                <View style={styles.skeletonEmpty}>
                  <AppSkeleton
                    mode="product_catalog"
                    count={16}
                    numColumns={numColumns}
                    isRowLayout={false}
                    showProductImages={showProductImages}
                  />
                </View>
              ) : null
            }
            ListFooterComponent={renderFooter}
          />
        </View>

        {/* A-Z Letter Rail */}
        {alphaRailEnabled && letters.length > 0 ? (
          <AlphaRail
            ref={alphaRailRef}
            letters={letters}
            onLetterPress={scrollToLetter}
          />
        ) : null}
      </View>
      {/* Floating Cart Button - only show on smaller screens */}
      {cartTotalQuantity > 0 && (
        <Pressable
          onPress={() => (navigation as any).navigate('Cart')}
          style={({ pressed }) => [
            styles.floatingCartBtn,
            pressed && styles.floatingCartBtnPressed,
          ]}>
          <View style={styles.floatingCartBadge}>
            <Text style={styles.floatingCartBadgeText}>
              {cartTotalQuantity}
            </Text>
          </View>
          <FontAwesome6
            name="basket-shopping"
            iconStyle="solid"
            size={iconSize(22)}
            color={theme.colors.surface}
          />
        </Pressable>
      )}

      <CustomBottomSheet
        bottomSheetModalRef={lastProductBottomSheetModalRef}
        snapPoints={['40%']}
        enableDynamicSizing={false}
        constrainWidth={true}
        handleSheetChanges={() => {}}
        header={
          <Typography style={styles.bottomSheetTitle}>Clear Basket?</Typography>
        }
        footer={
          <ClearBasketActions onClear={clearCart} onContinue={closeModal} />
        }>
        <View style={styles.bottomSheetBody}>
          <Typography style={styles.bottomSheetinfoText}>
            Hi {employeedata?.bp_employee_name}, Since this order is not
            completed, it will be automatically saved. To process another order,
            please clear the basket.
          </Typography>
        </View>
      </CustomBottomSheet>
      {/* Picker sheet */}
      <CustomBottomSheet
        bottomSheetModalRef={subcategoryPickerSheetRef}
        snapPoints={['56%']}
        enableDynamicSizing={false}
        constrainWidth={true}
        handleSheetChanges={index => {
          if (index === -1) setSubcategoryPickerOpen(false);
        }}
        header={
          <Typography
            style={[styles.bottomSheetTitle, { marginBottom: v(20) }]}>
            Select Category
          </Typography>
        }>
        <View style={styles.pickerBody}>
          <FlatList
            data={allSubCategories}
            keyExtractor={keyTopExtractor}
            renderItem={renderPickerItem}
            numColumns={2}
            columnWrapperStyle={styles.pickerRow}
            contentContainerStyle={styles.pickerListContent}
            showsHorizontalScrollIndicator={false}
          />
        </View>
      </CustomBottomSheet>
      <ProductDetailBottomSheet
        bottomSheetRef={productDetailBottomSheetRef}
        product={selectedProduct}
        handleSubproductQuantity={handleSubproductQuantity}
      />
      <CalculatorModal
        visible={calculatorOpen}
        onDismiss={() => setCalculatorOpen(false)}
        storageKey="calculator:web:sidebar"
      />
      <FilterModal />
    </View>
  );
};

export default ProductList;

const createStyles = (
  theme: any,
  screenWidth: number,
  isPortraitSidebarMode: boolean,
) => {
  const isLarge = screenWidth >= 1024;
  const isMedium = screenWidth >= 768;
  /** 768–1023: modestly wider search + slight pull so bar doesn’t feel disconnected */
  const isTabletCompact = isMedium && !isLarge;
  const colors = theme.colors;

  return StyleSheet.create({
    container: {
      flex: 1,
      flexDirection: 'column',
      backgroundColor: colors.backgroundGray100,
    },

    // Top Bar (status-bar inset applied inline via useSafeAreaInsets)
    topBar: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      // Align back button with sidebar item centerline
      paddingLeft: 0,
      paddingRight: s(8),
      backgroundColor: theme.colors.primaryDark,
    },
    topBarLeft: {
      flex: 1,
      maxWidth: isTabletCompact ? s(660) : s(620),
      flexDirection: 'row',
      alignItems: 'center',
      gap: s(10),
    },
    topBarBackSlot: {
      width: s(WEB_NAV_SIDEBAR_RAIL_S),
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0,
    },
    topBarBackButton: {
      width: s(36),
      height: s(36),
      borderRadius: theme.borderRadius.md,
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: 'rgba(255, 255, 255, 0.15)',
      flexShrink: 0,
    } as any,
    topBarBackButtonHovered: {
      backgroundColor: 'rgba(255, 255, 255, 0.25)',
    },
    topBarRight: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: isPortraitSidebarMode ? s(8) : s(12),
      ...(isTabletCompact && { marginLeft: s(-3) }),
    },
    topBarLogoWrapper: {
      paddingVertical: v(2),
    },
    topBarLogo: {
      width: s(36),
      height: s(36),
      borderRadius: theme.borderRadius.md,
      overflow: 'hidden',
      padding: s(3),
      backgroundColor: colors.primary,
    },
    topBarAction: {
      width: isPortraitSidebarMode ? s(34) : s(36),
      height: isPortraitSidebarMode ? s(34) : s(36),
      borderRadius: theme.borderRadius.md,
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: 'rgba(255, 255, 255, 0.15)',
    },
    topBarActionHovered: {
      backgroundColor: 'rgba(255, 255, 255, 0.25)',
    },

    // Search Container
    searchContainer: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: s(10),
      backgroundColor: colors.backgroundGray100,
      borderRadius: theme.borderRadius.md,
      paddingHorizontal: s(14),
      height: v(50),
    },
    searchSlot: {
      flex: 1,
      maxWidth: isTabletCompact ? s(660) : s(620),
    },
    searchContainerExpanded: {
      backgroundColor: colors.surface,
      borderColor: colors.surface,
      borderWidth: 0,
    },
    searchInput: {
      flex: 1,
      fontFamily: GTFonts.regular,
      fontSize: fs(14),
      color: colors.text,
      height: '100%',
      outlineStyle: 'none',
    } as any,
    searchClearBtn: {
      padding: s(4),
    },
    scanBtn: {
      padding: s(8),
      marginLeft: s(4),
      borderLeftWidth: borderWidth(1),
      borderLeftColor: colors.borderGray200,
    },

    // Network Indicator
    networkIndicator: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: isPortraitSidebarMode ? s(4) : s(5),
      paddingHorizontal: isPortraitSidebarMode ? s(8) : s(10),
      paddingVertical: isPortraitSidebarMode ? v(5) : v(6),
      borderRadius: theme.borderRadius.round,
    },
    networkOnline: {
      backgroundColor: 'rgba(255, 255, 255, 0.15)',
    },
    networkOffline: {
      backgroundColor: 'rgba(255, 100, 100, 0.25)',
    },
    networkText: {
      fontFamily: GTFonts.semibold,
      fontSize: fs(12),
      color: theme.colors.textInverse,
    },

    // Date/Time
    dateTimeContainer: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
      gap: s(5),
      paddingHorizontal: s(6),
      display: isLarge ? 'flex' : 'none',
    } as any,
    dateTimeText: {
      fontFamily: GTFonts.regular,
      fontSize: fs(13),
      lineHeight: fs(16),
      color: 'rgba(255, 255, 255, 0.85)',
      fontVariant: ['tabular-nums'],
    },
    // Keep time from shifting the top bar as seconds tick.
    timeText: {
      width: s(106),
      lineHeight: fs(16),
    },
    supportIconWrap: {
      position: 'relative' as const,
    },
    unreadDot: {
      position: 'absolute' as const,
      top: v(2),
      right: s(0),
      backgroundColor: '#F44336',
      width: s(8),
      height: s(8),
      borderRadius: s(4),
    },

    // Categories Section
    categoriesSection: {
      flexDirection: 'row',
      alignItems: 'center',
      backgroundColor: colors.surface,
      paddingVertical: s(10),
      borderBottomWidth: borderWidth(1),
      borderBottomColor: colors.border,
      gap: s(8),
    },
    categoryToggle: {
      marginLeft: s(16),
      width: s(36),
      height: s(36),
      borderRadius: theme.borderRadius.md,
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: colors.surfaceSecondary,
      borderWidth: borderWidth(1),
      borderColor: colors.border,
    },
    categoryToggleHovered: {
      backgroundColor: colors.border,
    },
    categoriesContent: {
      paddingRight: s(20),
      gap: s(8),
    },
    categoryPill: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: s(6),
      paddingHorizontal: s(12),
      paddingVertical: s(8),
      borderRadius: theme.borderRadius.md,
      borderWidth: borderWidth(1.5),
      borderColor: theme.colors.border,
      backgroundColor: colors.surface,
    },
    categoryPillActive: {
      borderColor: colors.primary,
      backgroundColor: colors.primary,
    },
    categoryPillHovered: {
      borderColor: colors.textSecondary,
      backgroundColor: colors.surfaceSecondary,
    },
    categoryLabel: {
      fontFamily: GTFonts.semibold,
      fontSize: fs(13),
      lineHeight: fs(16),
      color: colors.text,
      ...(Platform.OS === 'android' ? { includeFontPadding: false } : {}),
    },
    categoryLabelActive: {
      color: theme.colors.textInverse,
    },
    categoryCountBadge: {
      minWidth: s(22),
      height: v(22),
      borderRadius: theme.borderRadius.round,
      backgroundColor: colors.surfaceSecondary,
      alignItems: 'center',
      justifyContent: 'center',
      paddingHorizontal: s(5),
    },
    categoryCountBadgeActive: {
      backgroundColor: theme.colors.surface,
    },
    categoryCount: {
      fontFamily: GTFonts.semibold,
      fontSize: fs(11),
      lineHeight: fs(13),
      color: colors.textSecondary,
      ...(Platform.OS === 'android' ? { includeFontPadding: false } : {}),
    },
    categoryCountActive: {
      color: colors.primary,
    },

    // Products Grid
    productsArea: {
      flex: 1,
      flexDirection: 'row',
      backgroundColor: colors.backgroundGray100,
    },
    productsListContainer: {
      flex: 1,
    },
    gridContent: {
      paddingTop: v(10),
      paddingBottom: s(80),
      paddingHorizontal: s(2),
    },
    productItemWrapper: {
      paddingHorizontal: s(4),
      paddingBottom: s(8),
    },

    // Empty State
    emptyContainer: {
      flex: 1,
      alignItems: 'center',
      justifyContent: 'center',
      paddingVertical: s(80),
    },
    emptyAdminContainer: {
      flex: 1,
      alignItems: 'center',
      justifyContent: 'center',
      paddingVertical: s(80),
      gap: s(12),
    },
    emptyText: {
      fontFamily: GTFonts.semibold,
      fontSize: fs(18),
      lineHeight: fs(22),
      color: colors.text,
      marginTop: v(16),
    },
    emptySubtext: {
      fontFamily: GTFonts.regular,
      fontSize: fs(14),
      lineHeight: fs(20),
      color: colors.textSecondary,
      marginTop: v(8),
    },

    // Footer Add Card (Admin)
    footerAddCardWrapper: {
      alignItems: 'center',
      paddingVertical: s(16),
      paddingHorizontal: s(20),
    },
    addCard: {
      backgroundColor: colors.surface,
      borderRadius: theme.borderRadius.md,
      borderColor: colors.border,
      borderWidth: borderWidth(1),
      borderStyle: 'dashed',
      alignItems: 'center',
      justifyContent: 'center',
      paddingVertical: s(20),
      paddingHorizontal: s(24),
      minWidth: 200,
      maxWidth: WEB_PRODUCT_GRID_IMAGE_MAX_WIDTH,
    } as any,
    addCardHovered: {
      borderColor: colors.primary,
      backgroundColor: withOpacity(colors.primary, 0.05),
    },
    addCardTitle: {
      fontFamily: GTFonts.semibold,
      color: colors.text,
      fontSize: fs(14),
      lineHeight: fs(18),
    },
    addCardSubtitle: {
      fontFamily: GTFonts.regular,
      color: colors.textSecondary,
      marginTop: v(4),
      fontSize: fs(12),
      lineHeight: fs(16),
    },

    // Loading
    loadingFooter: {
      paddingVertical: s(24),
      alignItems: 'center',
      justifyContent: 'center',
    },
    skeletonEmpty: {
      paddingTop: v(6),
    },
    skeletonFooter: {
      paddingTop: v(10),
      paddingBottom: v(6),
    },

    // Floating Cart
    floatingCartBtn: {
      position: 'absolute',
      bottom: v(24),
      right: s(24),
      width: s(60),
      height: s(60),
      borderRadius: theme.borderRadius.md,
      backgroundColor: colors.success,
      alignItems: 'center',
      justifyContent: 'center',
    } as any,
    floatingCartBtnPressed: {
      transform: [{ scale: 0.95 }],
    },
    floatingCartBadge: {
      position: 'absolute',
      top: -v(4),
      right: -s(4),
      minWidth: s(24),
      height: s(24),
      borderRadius: theme.borderRadius.round,
      backgroundColor: colors.secondary,
      alignItems: 'center',
      justifyContent: 'center',
      paddingHorizontal: s(6),
      borderWidth: borderWidth(2),
      borderColor: colors.surface,
    },
    floatingCartBadgeText: {
      fontFamily: GTFonts.bold,
      fontSize: fs(12),
      color: colors.surface,
    },

    // Bottom Sheet
    bottomSheetTitle: {
      fontFamily: GTFonts.semibold,
      fontSize: fs(16),
      alignSelf: 'center',
      paddingVertical: s(8),
    },
    bottomSheetBody: {
      paddingHorizontal: s(16),
      paddingBottom: s(18),
      gap: v(14),
    },
    bottomSheetinfoText: {
      fontFamily: GTFonts.regular,
      fontSize: fs(14),
      color: colors.textSecondary,
      lineHeight: fs(20),
    },

    // Picker
    pickerBody: {
      paddingHorizontal: s(12),
      paddingBottom: s(12),
    },
    pickerListContent: {
      paddingBottom: s(18),
    },
    pickerRow: {
      justifyContent: 'space-between',
      gap: s(10),
    },
    pickerItem: {
      flex: 1,
      flexDirection: 'row',
      alignItems: 'center',
      gap: s(8),
      borderWidth: borderWidth(1),
      borderColor: colors.border,
      borderRadius: theme.borderRadius.md,
      paddingHorizontal: s(10),
      paddingVertical: s(20),
      marginBottom: v(10),
      backgroundColor: colors.surface,
    },
    pickerItemActive: {
      borderColor: colors.success,
      backgroundColor: colors.successLight,
    },
    pickerLabel: {
      fontFamily: GTFonts.semibold,
      color: colors.text,
      fontSize: fs(12),
      flex: 1,
    },
    pickerLabelActive: {
      color: colors.success,
    },
  });
};
